import { useState } from 'react';
import { Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { slugifyTerm } from '@/lib/utils';
import { legalTerms as allLegalTerms } from '@/data/legalTerms';
import { motion } from 'framer-motion';

// Function to render text with internal links
const renderTextWithLinks = (text: string) => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    // Add the link
    parts.push(
      <Link
        key={match.index}
        to={match[2]}
        className="underline text-primary hover:text-primary/80 transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        {match[1]}
      </Link>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts;
};

const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: delay / 1000 }}
  >
    {children}
  </motion.div>
);

const Dictionary = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Use shared legal terms dataset
  const legalTerms = allLegalTerms;


  const filteredTerms = legalTerms
    .filter(term =>
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.term.toLowerCase().localeCompare(b.term.toLowerCase()));

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <AnimatedSection delay={0}>
            <div className="text-center mb-12">
              <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-4">
                Words and Terms
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                An understanding of legal words and terms is paramount in our pursuit of the mastery of lawful self-governance in the land of obscured lawlessness.
              </p>
            </div>
          </AnimatedSection>

          {/* Search */}
          <AnimatedSection delay={200}>
            <div className="relative mb-8">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                type="text"
                placeholder="Search legal terms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </AnimatedSection>

          {/* Terms List */}
          <AnimatedSection delay={400}>
            <div className="md:columns-2 md:gap-8">
              {filteredTerms.map((item, index) => (
                <article key={index} className="relative hover:bg-muted/30 px-2 py-1 rounded transition-colors duration-200 break-inside-avoid mb-1">
                  <h3 className="font-bold text-lg text-primary underline">{item.term}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                    {renderTextWithLinks(item.definition)}
                  </p>
                  <Link
                    to={`/dictionary/${slugifyTerm(item.term)}`}
                    aria-label={`View definition of ${item.term}`}
                    className="absolute inset-0"
                  />
                </article>
              ))}
            </div>
          </AnimatedSection>

          {filteredTerms.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No terms found matching your search.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dictionary;